import { useState } from "react";
import { Landmark } from "lucide-react";
import { useCrm } from "../modal/CrmContext";
import Modal from "shared/Modal";
import Button from "shared/Button";
import Alert from "shared/Alert";
import { FORM_CONTROL } from "shared/tableStyles";

interface Props {
  onClose: () => void;
}

export default function CreateOpportunityModal({ onClose }: Props) {
  const { createOpportunity, pipeline } = useCrm();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = title.trim().length > 0;

  async function confirm() {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      await createOpportunity(title.trim(), description.trim() || undefined);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || "Failed to create opportunity. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      size="md"
      title="New Opportunity"
      description={pipeline?.name}
      icon={<Landmark />}
      onClick={saving ? undefined : onClose}
      closeButtonCLick={saving ? undefined : onClose}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={confirm} disabled={saving || !canSubmit}>
            {saving ? "Creating…" : "Create"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1.5">Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Community Arts Master Plan for Arts & Culture Department"
            className={`w-full px-3 py-2 ${FORM_CONTROL}`}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1.5">Description (optional)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className={`w-full px-3 py-2 ${FORM_CONTROL} resize-none`}
          />
        </div>
        <p className="text-[11px] text-muted-foreground">
          RFPs processed via the rfp slash commands register themselves automatically — manual creation is for anything that arrives outside that flow.
        </p>
      </div>

      {error && (
        <Alert tone="danger" className="mt-4">{error}</Alert>
      )}
    </Modal>
  );
}
